import { useState, useEffect } from "react";
import {
  TrendingUp, TrendingDown, DollarSign, Package,
  Users, Building2, AlertTriangle, Loader2,
  RefreshCw, Bot, ChevronRight, Receipt, Cog, FileText
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import axios from "axios";

export default function ERPHub() {
  const [dashboard, setDashboard] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, suggRes] = await Promise.all([
        axios.get("/api/erp/dashboard"),
        axios.get("/api/erp/suggestions"),
      ]);
      setDashboard(dashRes.data);
      setSuggestions(suggRes.data.suggestions || []);
    } catch (err) {
      toast({ title: "Failed to load ERP data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">ERP Dashboard</h1>
            <p className="text-muted-foreground">Enterprise Resource Planning overview</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: "Total Receivables",
      value: `$${(dashboard?.total_receivables || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
    },
    {
      title: "Total Payables",
      value: `$${(dashboard?.total_payables || 0).toLocaleString()}`,
      icon: TrendingDown,
      color: "text-red-600",
      bg: "bg-red-50",
      border: "border-red-200",
    },
    {
      title: "Net Position",
      value: `$${(dashboard?.net_position || 0).toLocaleString()}`,
      icon: DollarSign,
      color: (dashboard?.net_position || 0) >= 0 ? "text-blue-600" : "text-red-600",
      bg: "bg-blue-50",
      border: "border-blue-200",
    },
    {
      title: "Inventory Value",
      value: `$${(dashboard?.inventory_value || 0).toLocaleString()}`,
      icon: Package,
      color: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-200",
    },
    {
      title: "Customers",
      value: dashboard?.total_customers || 0,
      icon: Users,
      color: "text-indigo-600",
      bg: "bg-indigo-50",
      border: "border-indigo-200",
    },
    {
      title: "Vendors",
      value: dashboard?.total_vendors || 0,
      icon: Building2,
      color: "text-teal-600",
      bg: "bg-teal-50",
      border: "border-teal-200",
    },
    {
      title: "Open POs",
      value: dashboard?.open_pos || 0,
      icon: Package,
      color: "text-orange-600",
      bg: "bg-orange-50",
      border: "border-orange-200",
    },
    {
      title: "Overdue Invoices",
      value: dashboard?.overdue_invoices || 0,
      icon: AlertTriangle,
      color: (dashboard?.overdue_invoices || 0) > 0 ? "text-red-600" : "text-green-600",
      bg: (dashboard?.overdue_invoices || 0) > 0 ? "bg-red-50" : "bg-green-50",
      border: (dashboard?.overdue_invoices || 0) > 0 ? "border-red-200" : "border-green-200",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">ERP Dashboard</h1>
          <p className="text-muted-foreground">Enterprise Resource Planning overview</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi) => (
          <Card key={kpi.title} className={`${kpi.bg} ${kpi.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.title}</p>
                  <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                </div>
                <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-70`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-indigo-500" />
              AI Suggestions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg border hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => navigate(`/erp/${s.endpoint}`)}
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={s.type === "alert" ? "destructive" : s.type === "warning" ? "warning" : "secondary"}>
                      {s.type}
                    </Badge>
                    <span>{s.title}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-indigo-600">
                    {s.action} <ChevronRight className="h-4 w-4" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button variant="outline" className="h-20" onClick={() => navigate("/erp/finance")}>
              <Receipt className="h-5 w-5 mr-2" />
              Journal Entry
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/erp/finance")}>
              <Users className="h-5 w-5 mr-2" />
              AR Aging
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/erp/scm")}>
              <Package className="h-5 w-5 mr-2" />
              New Material
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/erp/scm")}>
              <Package className="h-5 w-5 mr-2" />
              Purchase Order
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/erp/production")}>
              <Cog className="h-5 w-5 mr-2" />
              New BOM
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/erp/production")}>
              <Cog className="h-5 w-5 mr-2" />
              Prod Order
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/erp/assistant")}>
              <Bot className="h-5 w-5 mr-2" />
              AI Assistant
            </Button>
            <Button variant="outline" className="h-20" onClick={() => navigate("/erp/documents")}>
              <FileText className="h-5 w-5 mr-2" />
              Doc Processor
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
